import React from "react";
import { TableColumnType } from "antd";
import xlsx from "better-xlsx";
import objectPath from "object-path";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import logoImage from "assets/img/logo-small.png";
import useCompanyInfo from "./useCompanyInfo";
import ExcelJS from "exceljs";
const useTableExport = ({ columns, data, fileName, pdfTheme }) => {
  const {
    companyName,
    address1,
    address2,
    address3,
    zipCode,
    country,
    phoneNo,
    email,
    companyLogo,
  } = useCompanyInfo();

  const onExcelPrint = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Sheet1");

    // Company name (left block)
    sheet.mergeCells("A1:C3");
    const leftCell = sheet.getCell("A1");
    leftCell.value = companyName;
    leftCell.font = { bold: true, size: 14 };
    leftCell.alignment = { horizontal: "left", vertical: "middle" };

    // Company info (right block)
    sheet.mergeCells("D1:H3");
    const rightCell = sheet.getCell("D1");
    rightCell.value = `${address1} \n ${address2} \n ${address3} \n ${country} - ${zipCode} \n Email : ${email} \n Phone : ${phoneNo}`;
    rightCell.alignment = {
      horizontal: "right",
      vertical: "middle",
      wrapText: true,
    };

    // Empty row for spacing
    sheet.addRow([]);

    // Table header row
    const headerRow = sheet.addRow(
      columns
        .filter(({ title }) => title !== "Action")
        .map(({ title }) => title)
    );
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };

    // Table data
    data.forEach((record) => {
      const rowValues = [];
      columns.forEach((col) => {
        if (col.title === "Action") return;
        let value = objectPath.get(record, col.dataIndex);
        if (col.render && col.title !== "Status") {
          value = col.render(value, record);
          if (React.isValidElement(value)) {
            value = value.props.children || "";
          }
        }
        if (Array.isArray(value) && value.length > 0) {
          value = value?.[1]?.toString() || "";
        }
        rowValues.push(value || "");
      });
      sheet.addRow(rowValues);
    });

    // Adjust column widths automatically (optional)
    sheet.columns.forEach((col) => {
      let maxLength = 0;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const len = cell.value ? cell.value.toString().length : 0;
        if (len > maxLength) maxLength = len;
      });
      col.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    // Save as file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${fileName}.xlsx`);
  };

  const onCsvPrint = () => {
    let csv = "";
    columns.forEach(({ title, render }, index) => {
      // if (render) return;
      if (title === "Action") return;
      if (index !== 0) csv += ",";

      csv += `${title.replaceAll('"', '""')}`;
    });

    csv += "\n";

    data.forEach((record) => {
      columns.forEach(({ title, dataIndex, render }, index) => {
        // if (render) return;
        if (title === "Action") return;

        if (index !== 0) csv += ",";

        csv += `${objectPath.get(record, dataIndex).replaceAll('"', '""')}`;
      });
      csv += "\n";
    });

    saveAs(new Blob([csv]), `${fileName}.csv`);
  };

  const onPdfPrint = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // === Logo ===
    const logoSize = 20;
    const marginTop = 10;
    const marginLeft = 15;
    if (companyLogo) {
      doc.addImage(
        companyLogo,
        "PNG",
        marginLeft,
        marginTop,
        logoSize,
        logoSize
      );
    }

    // === Left: Company Name ===
    const companyNameY = marginTop + logoSize / 2 + 3; // vertically centered with logo
    doc.setFontSize(14);
    doc.setFont("Helvetica", "bold");
    doc.text(
      companyName?.toUpperCase() || "",
      marginLeft + logoSize + 5,
      companyNameY
    );

    // === Right: Company Info ===
    const companyInfo = [
      address1,
      address2,
      address3,
      `${country} - ${zipCode}`,
      `Phone: ${phoneNo}`,
      `Email: ${email}`,
    ].filter(Boolean);

    doc.setFontSize(10);
    doc.setFont("Helvetica", "bold");

    const rightColumnX = pageWidth - 15; // right margin
    let currentY = marginTop + 3;

    companyInfo.forEach((line) => {
      doc.text(line, rightColumnX, currentY, { align: "right" });
      currentY += 5;
    });

    const headerBottomY = Math.max(marginTop + logoSize + 5, currentY);
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(15, headerBottomY, pageWidth - 15, headerBottomY);

    // === Table starts after line ===
    const tableStartY = headerBottomY + 5;

    // === Calculate dynamic table start position ===
    // const headerBottomY = Math.max(marginTop + logoSize + 5, currentY);
    // const tableStartY = headerBottomY + 5;

    // === Table ===
    autoTable(doc, {
      startY: tableStartY,
      styles: { font: "Helvetica", fontSize: 8, cellWidth: "auto" },
      headStyles: { fontStyle: "bold" },
      head: [columns.filter((c) => c.title !== "Action").map((c) => c.title)],
      body: data.map((row) =>
        columns
          .filter((c) => c.title !== "Action")
          .map((c) => objectPath.get(row, c.dataIndex) ?? "")
      ),
      theme: pdfTheme,
    });

    doc.save(`${fileName}.pdf`);
  };

  // const onPdfPrint = () => {
  //   const doc = new jsPDF();
  //   doc.setFont("FreeSans");

  //   const pdfLogo = companyLogo;
  //   const width = 15;
  //   const height = 15;

  //   doc.addImage(pdfLogo, "PNG", 15, 10, width, height);

  //   const companyInfo = [
  //     companyName,
  //     `${address1}`,
  //     `${address2}`,
  //     `${address3}`,
  //     `${country} - ${zipCode}`,
  //     `Phone: ${phoneNo}`,
  //     `Email: ${email}`,
  //   ];

  //   doc.setFontSize(10);
  //   const rightX = 35; // A4 width - margin
  //   const startY = 12;

  //   companyInfo.forEach((line, i) => {
  //     if (i === 0) {
  //       doc.setFontSize(14);
  //       doc.setFont("FreeSans", "bold");
  //       doc.text(line.toUpperCase(), rightX, 19, { align: "left" });
  //     } else {
  //       doc.setFontSize(10);
  //       doc.setFont("FreeSans", "normal");
  //       doc.text(line, 210 - 60, startY + i * 5, { align: "left" });
  //     }
  //   });

  //   const tableStartY = 50;

  //   autoTable(doc, {
  //     styles: { font: "FreeSans", fontSize: 8, cellWidth: "auto" },
  //     startY: tableStartY,
  //     headStyles: { fontStyle: "normal" },
  //     head: [columns.filter((c) => c.title !== "Action").map((c) => c.title)],
  //     body: data.map((r) =>
  //       columns
  //         //   .filter((c) => !c.render)
  //         .filter((c) => c.title !== "Action")
  //         .map((c) => objectPath.get(r, c.dataIndex))
  //     ),
  //     theme: pdfTheme,
  //   });

  //   doc.save(`${fileName}.pdf`);
  // };

  // const onPdfPrint = () => {

  //   const input = document.getElementById("pdfPrint");

  //   html2canvas(input, { scale: 2 }).then((canvas) => {
  //     const imgData = canvas.toDataURL("image/png");
  //     const pdf = new jsPDF("p", "mm", "a4");
  //     const imgProps = pdf.getImageProperties(imgData);
  //     const pdfWidth = pdf.internal.pageSize.getWidth();
  //     const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

  //     pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
  //     pdf.save(`${fileName}.pdf`);
  //   });
  // };

  const onAttendanceExcelDownload = () => {
    const file = new xlsx.File();
    const sheet = file.addSheet("Sheet1");

    // Add Header Row
    const headerRow = sheet.addRow();
    columns.forEach((data) => {
      if (data?.children) {
        const cell = headerRow.addCell();
        cell.value = "Date";
        data?.children.forEach(({ title }) => {
          const cell = headerRow.addCell();
          cell.value = title;
        });
      } else {
        const cell = headerRow.addCell();
        cell.value = data?.title;
      }
    });

    // Add Data Rows
    data.forEach((record) => {
      const row = sheet.addRow();
      columns.forEach((data) => {
        if (data?.children) {
          const cell = row.addCell();
          cell.value = data?.children?.[0]?.key.split("_")[0];
          data?.children.forEach(({ dataIndex, render }) => {
            const cell = row.addCell();

            let value = objectPath.get(record, dataIndex);
            console.log(dataIndex);
            if (render) {
              console.log(render);
              value = render(value, record);
              if (React.isValidElement(value)) {
                value = value.props.children || "";
              }
            }

            cell.value = value || "";
          });
        } else {
          const cell = row.addCell();
          let value = objectPath.get(record, data?.dataIndex);
          if (data?.render) {
            value = data.render(value, record);
            if (React.isValidElement(value)) {
              value = value.props.children || "";
            }
          }
          cell.value = value || "";
        }
      });
    });

    // Save Excel File
    file.saveAs("blob").then((blob) => {
      saveAs(blob, `${fileName}.xlsx`);
    });
  };

  const onJobExcelDownload = () => {
    const file = new xlsx.File();
    const sheet = file.addSheet("Sheet1");
    const headerRow = sheet.addRow();
    columns.forEach(({ title, render }) => {
      // if (render) return;
      if (title === "Action") return;
      const cell = headerRow.addCell();
      cell.value = title;
    });
    data.forEach((record) => {
      const row = sheet.addRow();
      columns.forEach((data) => {
        if (data.title === "Action") return;
        // columns.forEach(({ dataIndex, render }) => {
        // console.log(render)
        // if (render) return;
        // const cell = row.addCell();
        // cell.value = objectPath.get(record, dataIndex);
        const cell = row.addCell();
        let value = objectPath.get(record, data?.dataIndex);
        if (data?.render && data.title !== "Status") {
          value = data.render(value, record);
          if (React.isValidElement(value)) {
            value = value.props.children || "";
          }
        }
        cell.value = value || "";
      });
    });

    file.saveAs("blob").then((blob) => {
      saveAs(blob, `${fileName}.xlsx`);
    });
  };

  const onEmployeeExcelDownload = () => {
    // const file = new xlsx.File();
    // const sheet = file.addSheet("Sheet1");

    // const headerRow = sheet.addRow();
    // columns.forEach((column) => {
    //   const cell = headerRow.addCell();
    //   cell.value = column.title;
    // });

    // data.forEach((record) => {
    //   const row = sheet.addRow();
    //   columns.forEach((column) => {
    //     const cell = row.addCell();
    //     let value = objectPath.get(record, column.dataIndex);
    //     if (typeof value === "object" && value !== null) {
    //       value = JSON.stringify(value);
    //     }
    //     cell.value = value || "";
    //   });
    // });

    // file.saveAs("blob").then((blob) => {
    //   saveAs(blob, `${fileName}.xlsx`);
    // });

    let csv = "";

    // Add column headers
    columns.forEach(({ title }, index) => {
      if (index !== 0) csv += ",";
      csv += `"${title.replaceAll('"', '""')}"`; // Escape quotes in titles
    });

    csv += "\n";

    // Add rows
    data.forEach((record) => {
      columns.forEach(({ dataIndex }, index) => {
        if (index !== 0) csv += ",";

        // Safely retrieve and stringify the value
        let value = objectPath.get(record, dataIndex);

        if (value === undefined || value === null) {
          value = ""; // Replace null or undefined with empty string
        } else if (typeof value === "object") {
          value = JSON.stringify(value); // Convert objects/arrays to JSON strings
        }

        // Escape quotes in the value
        csv += `"${value.toString().replaceAll('"', '""')}"`;
      });

      csv += "\n";
    });

    // Save the CSV file
    saveAs(new Blob([csv], { type: "text/csv" }), `${fileName}.csv`);
  };

  const onEmployeeCountDownload = () => {   

    let csv = "";

    // Add column headers
    columns.forEach(({ title }, index) => {
      if (index !== 0) csv += ",";
      csv += `"${title.replaceAll('"', '""')}"`; // Escape quotes in titles
    });

    csv += "\n";

    // Add rows
    data.forEach((record) => {
      columns.forEach(({ dataIndex }, index) => {
        if (index !== 0) csv += ",";

        // Safely retrieve and stringify the value
        let value = objectPath.get(record, dataIndex);

        if (value === undefined || value === null) {
          value = ""; // Replace null or undefined with empty string
        } else if (typeof value === "object") {
          value = JSON.stringify(value); // Convert objects/arrays to JSON strings
        }

        // Escape quotes in the value
        csv += `"${value.toString().replaceAll('"', '""')}"`;
      });

      csv += "\n";
    });

    // Save the CSV file
    saveAs(new Blob([csv], { type: "text/csv" }), `${fileName}.csv`);
  };

  const onPrint = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // === Logo ===
    const logoSize = 20;
    const marginTop = 10;
    const marginLeft = 15;
    if (companyLogo) {
      doc.addImage(
        companyLogo,
        "PNG",
        marginLeft,
        marginTop,
        logoSize,
        logoSize
      );
    }

    // === Left: Company Name ===
    const companyNameY = marginTop + logoSize / 2 + 3; // vertically centered with logo
    doc.setFontSize(14);
    doc.setFont("Helvetica", "bold");
    doc.text(
      companyName?.toUpperCase() || "",
      marginLeft + logoSize + 5,
      companyNameY
    );

    // === Right: Company Info ===
    const companyInfo = [
      address1,
      address2,
      address3,
      `${country} - ${zipCode}`,
      `Phone: ${phoneNo}`,
      `Email: ${email}`,
    ].filter(Boolean);

    doc.setFontSize(10);
    doc.setFont("Helvetica", "normal");

    const rightColumnX = pageWidth - 15; // right margin
    let currentY = marginTop + 3;

    companyInfo.forEach((line) => {
      doc.text(line, rightColumnX, currentY, { align: "right" });
      currentY += 5;
    });

    const headerBottomY = Math.max(marginTop + logoSize + 5, currentY);
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(15, headerBottomY, pageWidth - 15, headerBottomY);

    // === Table starts after line ===
    const tableStartY = headerBottomY + 5;

    // === Table ===
    autoTable(doc, {
      startY: tableStartY,
      styles: { font: "Helvetica", fontSize: 8, cellWidth: "auto" },
      headStyles: { fontStyle: "bold" },
      head: [columns.filter((c) => c.title !== "Action").map((c) => c.title)],
      body: data.map((row) =>
        columns
          .filter((c) => c.title !== "Action")
          .map((c) => objectPath.get(row, c.dataIndex) ?? "")
      ),
      theme: pdfTheme,
    });

    // doc.save(`${fileName}.pdf`);

    window.open(doc.output("bloburl"));
  };

  return {
    onExcelPrint,
    onCsvPrint,
    onPdfPrint,
    onAttendanceExcelDownload,
    onJobExcelDownload,
    onEmployeeExcelDownload,
    onEmployeeCountDownload,
    onPrint,
  };
};

export default useTableExport;

// const onExcelPrint = () => {
//   const file = new xlsx.File();
//   const sheet = file.addSheet("Sheet1");

//   const headerRow = sheet.addRow();
//   columns.forEach(({ title, render }) => {
//     if (title === "Action") return;
//     // if (render) return;
//     const cell = headerRow.addCell();
//     cell.value = title;
//   });
//   data.forEach((record) => {
//     const row = sheet.addRow();
//     columns.forEach((data) => {
//       if (data?.title === "Action") return;
//       // columns.forEach(({ dataIndex, render }) => {
//       // console.log(render)
//       // if (render) return;
//       // const cell = row.addCell();
//       // cell.value = objectPath.get(record, dataIndex);
//       const cell = row.addCell();
//       let value = objectPath.get(record, data?.dataIndex);
//       if (data?.render && data?.title !== "Status") {
//         value = data.render(value, record);

//         if (React.isValidElement(value)) {
//           value = value.props.children || "";
//         }
//       }

//       if (Array.isArray(value) && value.length > 0) {
//         // value = JSON.stringify(value);
//         value = value?.[1]?.toString() || "";
//       }

//       cell.value = value || "";
//     });
//   });

//   file.saveAs("blob").then((blob) => {
//     saveAs(blob, `${fileName}.xlsx`);
//   });
// };

// const onCsvPrint = () => {
//   let csv = "";

//   // Add headers
//   columns.forEach(({ title }, index) => {
//     if (title === "Action") return; // Skip the "Action" column

//     if (index !== 0) csv += ",";
//     csv += `"${title.replaceAll('"', '""')}"`; // Escape double quotes in headers
//   });

//   csv += "\n";

//   // Add data rows
//   data.forEach((record) => {
//     columns.forEach(({ title, dataIndex, render }, index) => {
//       if (title === "Action") return; // Skip the "Action" column

//       if (index !== 0) csv += ",";

//       let value;

//       // Handle rendered data
//       if (render) {
//         const renderedValue = render(
//           objectPath.get(record, dataIndex),
//           record,
//           index
//         );

//         // Convert rendered output to a string
//         if (React.isValidElement(renderedValue)) {
//           // If it's a React element, extract its text or props.children
//           value = renderedValue.props?.children || "";
//         } else if (typeof renderedValue === "object") {
//           // If it's an object, stringify it
//           value = JSON.stringify(renderedValue);
//         } else {
//           // Otherwise, use it as-is (string/number)
//           value = renderedValue;
//         }
//       } else {
//         // Fallback to raw data for non-render columns
//         value = objectPath.get(record, dataIndex) ?? "";
//       }

//       // Ensure value is converted to a string and properly escaped
//       csv += `"${value.toString().replaceAll('"', '""')}"`;
//     });
//     csv += "\n";
//   });

//   // Save as CSV file
//   saveAs(
//     new Blob([csv], { type: "text/csv;charset=utf-8;" }),
//     `${fileName}.csv`
//   );
// };
